/**
 * Clubs — owner-facing rolodex of external partner clubs + their contacts.
 *
 * Sachin spec 2026-02-17: "A rolodex of people I need to add and keep their
 * info. Club Name as the main thing to look at; expand to show people:
 * Name, Phone, Email, Position."
 *
 * Structure:
 *   - Top-level list of clubs (sorted by name).
 *   - Click a club row to expand its contacts.
 *   - "+ Add club" at the top of the page.
 *   - "+ Add person" inside each expanded club.
 *   - Pencil to edit, trash to delete (with confirms).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Plus, ChevronDown, ChevronRight, Mail, Phone, Pencil, Trash2,
    Loader2, X, Save, Users, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Modal({ open, onClose, children, title, busy }) {
    if (!open) return null;
    return (
        <div
            data-testid="club-modal-backdrop"
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !busy && onClose()}
        >
            <div
                className="bg-zinc-950 border border-zinc-800 max-w-md w-full p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <h3 className="font-display text-lg text-white tracking-wide">{title}</h3>
                    <button
                        type="button"
                        onClick={() => !busy && onClose()}
                        className="text-zinc-500 hover:text-white"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1">{label}</div>
            {children}
        </div>
    );
}

const inputCls = "w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red";

function ClubForm({ open, onClose, initial, onSaved }) {
    const isEdit = Boolean(initial);
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open) {
            setName(initial?.name || "");
            setNotes(initial?.notes || "");
        }
    }, [open, initial]);

    const submit = async () => {
        if (!name.trim()) {
            toast.error("Club name is required");
            return;
        }
        setBusy(true);
        try {
            const url = isEdit
                ? `${API}/coach/clubs/${initial.club_id}`
                : `${API}/coach/clubs`;
            const method = isEdit ? "patch" : "post";
            const r = await axios[method](url, { name: name.trim(), notes: notes.trim() }, { withCredentials: true });
            toast.success(isEdit ? "Club updated" : "Club added");
            onSaved(r.data);
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error("Couldn't save club", {
                description: (d && typeof d === "object" ? d.message : d) || "Unknown error",
            });
        } finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={onClose} title={isEdit ? "Edit club" : "Add club"} busy={busy}>
            <div className="space-y-3">
                <Field label="Club name" testid="club-form-name-field">
                    <input
                        data-testid="club-form-name"
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Pomona High Football"
                        className={inputCls}
                    />
                </Field>
                <Field label="Notes (optional)" testid="club-form-notes-field">
                    <textarea
                        data-testid="club-form-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Anything to remember about this club"
                        rows={3}
                        className={`${inputCls} resize-y`}
                    />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={busy}
                    className="text-[11px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white px-3 py-2"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={submit}
                    disabled={busy || !name.trim()}
                    data-testid="club-form-submit"
                    className="inline-flex items-center gap-2 bg-brand-red hover:bg-red-700 text-white text-[11px] tracking-[0.18em] uppercase font-bold px-4 py-2 disabled:opacity-50"
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {isEdit ? "Save" : "Add club"}
                </button>
            </div>
        </Modal>
    );
}

function ContactForm({ open, onClose, clubId, initial, onSaved }) {
    const isEdit = Boolean(initial);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [position, setPosition] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setName(initial?.name || "");
            setEmail(initial?.email || "");
            setPhone(initial?.phone || "");
            setPosition(initial?.position || "");
            setNotes(initial?.notes || "");
        }
    }, [open, initial]);

    const submit = async () => {
        if (!name.trim()) {
            toast.error("Name is required");
            return;
        }
        setBusy(true);
        try {
            const url = isEdit
                ? `${API}/coach/clubs/${clubId}/contacts/${initial.contact_id}`
                : `${API}/coach/clubs/${clubId}/contacts`;
            const method = isEdit ? "patch" : "post";
            const body = {
                name: name.trim(),
                email: email.trim(),
                phone: phone.trim(),
                position: position.trim(),
                notes: notes.trim(),
            };
            const r = await axios[method](url, body, { withCredentials: true });
            toast.success(isEdit ? "Contact updated" : "Person added");
            onSaved(r.data);
            onClose();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail || "Unknown error" });
        } finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={onClose} title={isEdit ? "Edit person" : "Add person to club"} busy={busy}>
            <div className="space-y-3">
                <Field label="Name" testid="contact-form-name-field">
                    <input data-testid="contact-form-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
                </Field>
                <Field label="Position" testid="contact-form-position-field">
                    <input data-testid="contact-form-position" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Owner, Head Coach, Athletic Director…" className={inputCls} />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Phone" testid="contact-form-phone-field">
                        <input data-testid="contact-form-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" className={inputCls} />
                    </Field>
                    <Field label="Email" testid="contact-form-email-field">
                        <input data-testid="contact-form-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@club.com" className={inputCls} />
                    </Field>
                </div>
                <Field label="Notes (optional)" testid="contact-form-notes-field">
                    <textarea data-testid="contact-form-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${inputCls} resize-y`} />
                </Field>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={onClose} disabled={busy} className="text-[11px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white px-3 py-2">Cancel</button>
                <button
                    type="button"
                    onClick={submit}
                    disabled={busy || !name.trim()}
                    data-testid="contact-form-submit"
                    className="inline-flex items-center gap-2 bg-brand-red hover:bg-red-700 text-white text-[11px] tracking-[0.18em] uppercase font-bold px-4 py-2 disabled:opacity-50"
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {isEdit ? "Save" : "Add person"}
                </button>
            </div>
        </Modal>
    );
}

function ContactRow({ contact, clubId, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const remove = async () => {
        if (!window.confirm(`Remove ${contact.name} from this club?`)) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/coach/clubs/${clubId}/contacts/${contact.contact_id}`, { withCredentials: true });
            toast.success("Contact removed");
            onChanged();
        } catch (e) {
            toast.error("Couldn't remove", { description: e?.response?.data?.detail });
        } finally { setDeleting(false); }
    };

    return (
        <>
            <div
                data-testid={`contact-row-${contact.contact_id}`}
                className="grid grid-cols-12 gap-3 items-center py-2.5 px-3 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/40"
            >
                <div className="col-span-12 sm:col-span-3 min-w-0">
                    <div className="text-sm text-white font-medium truncate" data-testid="contact-row-name">{contact.name}</div>
                    {contact.position && (
                        <div className="text-[10px] tracking-[0.18em] uppercase text-amber-300/90 mt-0.5" data-testid="contact-row-position">
                            {contact.position}
                        </div>
                    )}
                </div>
                <div className="col-span-12 sm:col-span-3 min-w-0">
                    {contact.phone ? (
                        <a
                            href={`tel:${contact.phone.replace(/\D/g, "")}`}
                            data-testid="contact-row-phone"
                            className="text-[12px] text-zinc-200 hover:text-brand-red inline-flex items-center gap-1.5 truncate"
                        >
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{contact.phone}</span>
                        </a>
                    ) : <span className="text-[11px] text-zinc-600">—</span>}
                </div>
                <div className="col-span-12 sm:col-span-4 min-w-0">
                    {contact.email ? (
                        <a
                            href={`mailto:${contact.email}`}
                            data-testid="contact-row-email"
                            className="text-[12px] text-zinc-200 hover:text-brand-red inline-flex items-center gap-1.5 truncate"
                        >
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{contact.email}</span>
                        </a>
                    ) : <span className="text-[11px] text-zinc-600">—</span>}
                </div>
                <div className="col-span-12 sm:col-span-2 flex items-center justify-end gap-1">
                    <button
                        type="button"
                        data-testid={`contact-row-edit-${contact.contact_id}`}
                        onClick={() => setEditing(true)}
                        className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded"
                        title="Edit"
                    >
                        <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        data-testid={`contact-row-delete-${contact.contact_id}`}
                        onClick={remove}
                        disabled={deleting}
                        className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded disabled:opacity-50"
                        title="Remove"
                    >
                        {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>
            <ContactForm
                open={editing}
                onClose={() => setEditing(false)}
                clubId={clubId}
                initial={contact}
                onSaved={onChanged}
            />
        </>
    );
}

function ClubCard({ club, defaultOpen, onChanged }) {
    const [open, setOpen] = useState(!!defaultOpen);
    const [loaded, setLoaded] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/clubs/${club.club_id}/contacts`, { withCredentials: true });
            setContacts(r.data?.contacts || []);
            setLoaded(true);
        } catch (e) {
            toast.error("Couldn't load contacts", { description: e?.response?.data?.detail });
        } finally { setLoading(false); }
    }, [club.club_id]);

    useEffect(() => {
        if (open && !loaded) reload();
    }, [open, loaded, reload]);

    const remove = async () => {
        if (!window.confirm(`Delete the club "${club.name}" and all its contacts? This can't be undone.`)) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/coach/clubs/${club.club_id}`, { withCredentials: true });
            toast.success("Club deleted");
            onChanged();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        } finally { setDeleting(false); }
    };

    const handleContactChanged = () => { reload(); onChanged(); };

    return (
        <div
            data-testid={`club-card-${club.club_id}`}
            className="border border-zinc-800 bg-zinc-950/60 mb-3"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={`club-toggle-${club.club_id}`}
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-zinc-900/60 transition-colors"
            >
                <div className="flex items-center gap-3 min-w-0">
                    {open ? <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />}
                    <div className="min-w-0 text-left">
                        <div className="font-display text-lg text-white tracking-wide truncate" data-testid="club-card-name">{club.name}</div>
                        {club.notes && !open && (
                            <div className="text-[11px] text-zinc-500 truncate mt-0.5">{club.notes}</div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-zinc-400 border border-zinc-700 px-2 py-1">
                        <Users className="w-3 h-3" />
                        {club.contact_count ?? contacts.length} {(club.contact_count ?? contacts.length) === 1 ? "person" : "people"}
                    </span>
                </div>
            </button>

            {open && (
                <div className="border-t border-zinc-800">
                    {club.notes && (
                        <div className="px-4 py-2 text-[11px] text-zinc-400 bg-zinc-950 border-b border-zinc-900 italic">
                            {club.notes}
                        </div>
                    )}
                    <div className="bg-black/30">
                        {loading ? (
                            <div className="text-xs text-zinc-500 py-4 text-center" data-testid="club-contacts-loading">Loading…</div>
                        ) : contacts.length === 0 ? (
                            <div className="text-xs text-zinc-500 py-5 text-center" data-testid={`club-contacts-empty-${club.club_id}`}>
                                No contacts yet — add the first person below.
                            </div>
                        ) : (
                            contacts.map((c) => (
                                <ContactRow key={c.contact_id} contact={c} clubId={club.club_id} onChanged={handleContactChanged} />
                            ))
                        )}
                    </div>
                    <div className="flex items-center gap-2 px-4 py-3 border-t border-zinc-800 bg-zinc-950/40">
                        <button
                            type="button"
                            data-testid={`club-add-person-${club.club_id}`}
                            onClick={() => setAdding(true)}
                            className="inline-flex items-center gap-1.5 bg-brand-red hover:bg-red-700 text-white text-[10px] tracking-[0.18em] uppercase font-bold px-3 py-1.5"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Add person
                        </button>
                        <div className="flex-1" />
                        <button
                            type="button"
                            data-testid={`club-edit-${club.club_id}`}
                            onClick={() => setEditing(true)}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white px-2 py-1.5"
                        >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit club
                        </button>
                        <button
                            type="button"
                            data-testid={`club-delete-${club.club_id}`}
                            onClick={remove}
                            disabled={deleting}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-red-400 hover:text-red-300 px-2 py-1.5 disabled:opacity-50"
                        >
                            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            Delete
                        </button>
                    </div>
                </div>
            )}

            <ContactForm open={adding} onClose={() => setAdding(false)} clubId={club.club_id} onSaved={handleContactChanged} />
            <ClubForm open={editing} onClose={() => setEditing(false)} initial={club} onSaved={() => { setEditing(false); onChanged(); }} />
        </div>
    );
}

export default function Clubs() {
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [adding, setAdding] = useState(false);
    const [filter, setFilter] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/clubs`, { withCredentials: true });
            setClubs(r.data?.clubs || []);
            setErr(null);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to load clubs");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = filter.trim()
        ? clubs.filter((c) => c.name.toLowerCase().includes(filter.toLowerCase()))
        : clubs;

    return (
        <div data-testid="clubs-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                    <h1 className="font-display text-3xl tracking-wider text-white">Clubs</h1>
                    <p className="text-[11px] text-zinc-500 mt-1 tracking-wide">
                        Rolodex of partner clubs, schools, and contacts.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        data-testid="clubs-filter"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search clubs…"
                        className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red w-48 sm:w-64"
                    />
                    <button
                        type="button"
                        data-testid="clubs-add"
                        onClick={() => setAdding(true)}
                        className="inline-flex items-center gap-2 bg-brand-red hover:bg-red-700 text-white text-[11px] tracking-[0.18em] uppercase font-bold px-4 py-2"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add club
                    </button>
                </div>
            </div>

            {err && (
                <div className="border border-red-700/60 bg-red-900/20 px-4 py-3 mb-4 text-sm text-red-200 flex items-center gap-2" data-testid="clubs-error">
                    <AlertTriangle className="w-4 h-4" />
                    {err}
                </div>
            )}

            {loading ? (
                <div className="text-zinc-500 text-sm text-center py-10" data-testid="clubs-loading">Loading…</div>
            ) : filtered.length === 0 ? (
                <div
                    data-testid="clubs-empty"
                    className="border border-dashed border-zinc-800 bg-zinc-950/40 px-6 py-12 text-center"
                >
                    <Users className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                    <div className="text-sm text-zinc-300">
                        {filter ? `No clubs matching "${filter}"` : "No clubs yet."}
                    </div>
                    {!filter && (
                        <div className="text-[12px] text-zinc-500 mt-1">
                            Click <strong className="text-white">Add club</strong> above to create your first one.
                        </div>
                    )}
                </div>
            ) : (
                <div data-testid="clubs-list">
                    {filtered.map((c) => (
                        <ClubCard
                            key={c.club_id}
                            club={c}
                            defaultOpen={filtered.length === 1}
                            onChanged={load}
                        />
                    ))}
                </div>
            )}

            <ClubForm open={adding} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
        </div>
    );
}
